import * as React from 'react';
import {
  Dropdown, DropdownItemProps, DropdownProps, Icon, SemanticICONS, SemanticWIDTHS, Table
} from 'semantic-ui-react';
import {
  CONFIG_VIEW_TEXT,
  IL_SOURCE_INDICATOR_ID_OPTIONS,
  IL_DESTINATION_INDICATOR_ID_OPTIONS,
  IL_KEY,
  IL_MAPPING,
  IL_MAPPING_OPTIONS,
  NO_MAPPING_OPTION
} from '../../../constants';
import { format, getOptions } from '../../../functions';
import './il-mapping-options.less';

/**
 * Column mapping configuration options for Indicator-Indicator Links
 */
export class ILMappingOptions extends React.Component<IILMappingOptionsProps, IILMappingOptionsState> {
  constructor(props: IILMappingOptionsProps) {
    super(props);
    this.state = {
      ilKey: this.getIlKey(props),
      mappingType: this.getDefaultType(),
      mappingTypeOptions: this.getTypeOptions(),
      warning: false
    };

    this.onTypeChange = this.onTypeChange.bind(this);
    this.onCoreEventColMappingChange = this.onCoreEventColMappingChange.bind(this);
  }

  /** "Type" option can be reset using NO_MAPPING */
  public getTypeOptions(): DropdownItemProps[] {
    const options: DropdownItemProps[] = getOptions(IL_MAPPING_OPTIONS);
    options.push({ value: NO_MAPPING_OPTION.key, text: NO_MAPPING_OPTION.caption });
    return options;
  }

  public getIlKey(props: IILMappingOptionsProps): string {
    return props.eventEventConfig[props.colIndex] || NO_MAPPING_OPTION.key;
  }

  public getDefaultType(): string {
    const key: string = this.getIlKey(this.props);
    let type: string = NO_MAPPING_OPTION.key;
    if (IL_SOURCE_INDICATOR_ID_OPTIONS[key]) { type = IL_MAPPING.INDICATOR_ID; }
    if (IL_DESTINATION_INDICATOR_ID_OPTIONS[key]) { type = IL_MAPPING.INDICATOR_ID_2; }

    return type;
  }

  public onTypeChange(e: React.ChangeEvent<HTMLDivElement>, changes: DropdownProps): void {
    const warning: boolean = !(changes.value.toString() === NO_MAPPING_OPTION.key);
    this.setState({
      mappingType: changes.value.toString(),
      ilKey: NO_MAPPING_OPTION.key
    });
    this.props.onMappingReset(this.props.colIndex);
    this.onWarningChange(warning);
  }

  /** Update own value and call parent callback */
  public onCoreEventColMappingChange(e: React.ChangeEvent<HTMLDivElement>, changes: DropdownProps): void {
    this.setState({ ilKey: changes.value.toString() });
    this.props.onEventEventColMappingChange(this.props.colIndex, changes.value.toString() as IL_KEY);
    this.onWarningChange(false);
  }

  /** Warning state shows or hides error sign for current row */
  public onWarningChange(warning: boolean): void {
    this.setState({ warning });
    this.props.onWarningChange(warning);
  }

  /**
   * Get status for column mapping (done, empty or warning)
   * Empty rows are allowed, the ones with warning are not
   */
  public getStatus(): JSX.Element {
    const STATUS_CELL_WIDTH: SemanticWIDTHS = 1;
    const empty: boolean = this.state.mappingType === NO_MAPPING_OPTION.key;
    const icon: SemanticICONS = (empty)
      ? 'warning'
      : (this.state.warning)
        ? 'warning sign'
        : 'checkmark';

    return (
      <Table.Cell
        textAlign="center"
        negative={!empty && this.state.warning}
        positive={!empty && !this.state.warning}
        width={STATUS_CELL_WIDTH}
      >
        <Icon name={icon} />
      </Table.Cell>
    );
  }

  public getIiOptions(): JSX.Element {
    let options: DropdownItemProps[] = [];
    switch (this.state.mappingType) {
      case IL_MAPPING.INDICATOR_ID:
        options = getOptions(IL_SOURCE_INDICATOR_ID_OPTIONS);
        break;
      case IL_MAPPING.INDICATOR_ID_2:
        options = getOptions(IL_DESTINATION_INDICATOR_ID_OPTIONS);
        break;
      default: {
        return null;
      }
    }

    return (
      <Dropdown
        options={options}
        value={this.state.ilKey}
        onChange={this.onCoreEventColMappingChange}
        selectOnBlur={false}
        selection
        search
        fluid
      />
    );
  }

  public render(): JSX.Element {
    const name: string = this.props.name
      || format(CONFIG_VIEW_TEXT.COLUMN_INDEX, { index: (Number(this.props.colIndex) || 0) + 1 });

    return (
      <Table.Row data-component="il-mapping-options">
        {this.getStatus()}

        <Table.Cell width={5}>
          {name}
        </Table.Cell>

        <Table.Cell width={5}>
          <Dropdown
            options={this.state.mappingTypeOptions}
            value={this.state.mappingType}
            onChange={this.onTypeChange}
            selectOnBlur={false}
            selection
            fluid
          />
        </Table.Cell>

        <Table.Cell width={5}>
          {this.getIiOptions()}
        </Table.Cell>
      </Table.Row>
    );
  }
}

export interface IILMappingOptionsState {
  readonly ilKey: string;
  /** Used to display row warnings or errors */
  readonly warning: boolean;
  readonly mappingType: string;
  readonly mappingTypeOptions: DropdownItemProps[];
}

export interface IILMappingOptionsProps {
  readonly colIndex: string;
  readonly name: string;
  readonly eventEventConfig: { [key: number]: IL_KEY };

  onMappingReset(colIndex: string): void;
  onWarningChange(warning: boolean): void;
  onEventEventColMappingChange(colIndex: string, key: IL_KEY): void;
}
